import {
  DuctingComponentProductType,
  DuctingComponentRoleType,
  RadialDuctMetricsType
} from 'data/types';
import {
  DuctRunSuppliesType,
  DuctSuppliesModeType,
  DuctSupplyLineKindType,
  DuctSupplyLineStatusType,
  FLEX_TAIL_LENGTH_FT,
  RETURN_REDUCER_LARGE_IN,
  RETURN_REDUCER_SMALL_IN,
  ZoneDuctSuppliesType
} from 'utils/duct-supplies';
import {
  NeededDuctingKit,
  NeededZoneDuctingProducts
} from 'utils/ducting-products';
import {
  largestPlannedSizeIn,
  returnBranchSizeIn
} from 'utils/return-branch-size';

export const DUCT_SOURCING_NOTICE =
  'Some parts will need to be sourced separately (e.g. from a local hardware store).';

export const DUCT_NOT_QUOTED_NOTICE =
  'Some calculated parts are not included in this quote — add them on the Accessories step.';

export const DUCT_NOT_QUOTED_NOTICE_CUSTOMER =
  'Some parts are not included in this quote and will need to be sourced separately (e.g. from a local hardware store).';

export const DUCT_METRICS_UNAVAILABLE_NOTICE =
  'Duct sizing could not be calculated for this air handler. Required ducting will need to be determined during installation.';

const DEFAULT_FLEX_BOX_LENGTH_FT = 25;
const DEFAULT_PIPE_JOINT_LENGTH_FT = 2;
const RETURN_SPLIT_OPTIONS = [1, 2, 3, 4];
const CONSUMABLE_RUNS_PER_UNIT = 8;

const PANEL_MATCH_TOLERANCE_IN = 1;

export function fittingComponents(
  catalog: DuctingComponentProductType[],
  role: DuctingComponentRoleType,
  sizeIn: number
): DuctingComponentProductType[] {
  return catalog
    .filter(
      (component) =>
        component.role === role && component.ductSizesIn.includes(sizeIn)
    )
    .sort((a, b) => a.price - b.price);
}

export function returnReducerComponents(
  catalog: DuctingComponentProductType[]
): DuctingComponentProductType[] {
  return catalog
    .filter(
      (component) =>
        component.role === 'reducer' &&
        component.ductSizesIn.includes(RETURN_REDUCER_LARGE_IN) &&
        component.ductSizesIn.includes(RETURN_REDUCER_SMALL_IN)
    )
    .sort((a, b) => a.price - b.price);
}

function parsePanelDimensions(panelSize: string | null): number[] {
  if (!panelSize) return [];
  return panelSize
    .split(/x/i)
    .map((token) => Number(token.trim()))
    .filter((value) => Number.isFinite(value) && value > 0);
}

export function pickFilterGrilleForBox(
  catalog: DuctingComponentProductType[],
  box: DuctingComponentProductType
): DuctingComponentProductType | null {
  if (box.openingWidthIn === null || box.openingDepthIn === null) return null;
  const boxDims = [box.openingWidthIn, box.openingDepthIn].sort(
    (a, b) => a - b
  );
  const matching = catalog
    .filter((component) => component.role === 'filter_grille')
    .filter((component) => {
      const panelDims = parsePanelDimensions(component.panelSize)
        .slice(0, 2)
        .sort((a, b) => a - b);
      if (panelDims.length < 2) return false;
      return (
        Math.abs(panelDims[0] - boxDims[0]) <= PANEL_MATCH_TOLERANCE_IN &&
        Math.abs(panelDims[1] - boxDims[1]) <= PANEL_MATCH_TOLERANCE_IN
      );
    })
    .sort((a, b) => a.price - b.price);
  return matching[0] ?? null;
}

function lineCandidateProducts(
  kind: DuctSupplyLineKindType,
  sizeIn: number,
  catalog: DuctingComponentProductType[]
): DuctingComponentProductType[] {
  if (kind === 'adapter') return [];
  if (kind === 'metal_duct') {
    return fittingComponents(catalog, 'round_pipe', sizeIn);
  }
  if (kind === 'flex_duct') {
    return fittingComponents(catalog, 'flex_duct', sizeIn);
  }
  if (kind === 'elbow') {
    return fittingComponents(catalog, 'elbow', sizeIn);
  }
  if (kind === 'register' || kind === 'boot') {
    return fittingComponents(catalog, 'ceiling_diffuser', sizeIn);
  }
  if (kind === 'grille_box') {
    return fittingComponents(catalog, 'return_box', sizeIn);
  }
  if (kind === 'reducer') {
    return returnReducerComponents(catalog);
  }
  return fittingComponents(catalog, 'return_box', sizeIn)
    .map((box) => pickFilterGrilleForBox(catalog, box))
    .filter((grille): grille is DuctingComponentProductType => grille !== null);
}

export function lineStockStatus(
  kind: DuctSupplyLineKindType,
  sizeIn: number,
  catalog: DuctingComponentProductType[]
): DuctSupplyLineStatusType {
  return lineCandidateProducts(kind, sizeIn, catalog).length > 0
    ? 'found'
    : 'missing';
}

export function lineQuotedStatus(
  kind: DuctSupplyLineKindType,
  sizeIn: number,
  catalog: DuctingComponentProductType[],
  quotedProductIds: ReadonlySet<number>
): DuctSupplyLineStatusType {
  return lineCandidateProducts(kind, sizeIn, catalog).some((candidate) =>
    quotedProductIds.has(candidate.productId)
  )
    ? 'found'
    : 'missing';
}

export type NeededDuctComponentType = {
  product: DuctingComponentProductType;
  quantity: number;
};

function addNeeded(
  target: Map<number, NeededDuctComponentType>,
  product: DuctingComponentProductType | null | undefined,
  quantity: number
): void {
  if (!product || quantity <= 0) return;
  const existing = target.get(product.productId);
  if (existing) {
    existing.quantity += quantity;
  } else {
    target.set(product.productId, { product, quantity });
  }
}

function pickTakeOff(
  catalog: DuctingComponentProductType[],
  sizeIn: number,
  preferDamper: boolean
): DuctingComponentProductType | null {
  const fitting = fittingComponents(catalog, 'take_off', sizeIn);
  if (fitting.length === 0) return null;
  const preferred = fitting.filter(
    (component) => component.hasDamper === preferDamper
  );
  return preferred[0] ?? fitting[0];
}

function boxesForLength(totalLengthFt: number, unitLengthFt: number): number {
  return Math.max(1, Math.ceil(Math.max(totalLengthFt, 0) / unitLengthFt));
}

function resolveReturnBoxForSize(
  catalog: DuctingComponentProductType[],
  sizeIn: number,
  mode: DuctSuppliesModeType
): {
  box: DuctingComponentProductType;
  reducer: DuctingComponentProductType | null;
} | null {
  const direct = fittingComponents(catalog, 'return_box', sizeIn)[0];
  if (direct) return { box: direct, reducer: null };
  if (mode !== 'flex_only' || sizeIn !== RETURN_REDUCER_SMALL_IN) return null;
  const box = fittingComponents(
    catalog,
    'return_box',
    RETURN_REDUCER_LARGE_IN
  )[0];
  const reducer = returnReducerComponents(catalog)[0];
  return box && reducer ? { box, reducer } : null;
}

function cheapestByRole(
  catalog: DuctingComponentProductType[],
  role: DuctingComponentRoleType
): DuctingComponentProductType | null {
  const matching = catalog
    .filter((component) => component.role === role)
    .sort((a, b) => a.price - b.price);
  return matching[0] ?? null;
}

function addSupplyComponents(
  target: Map<number, NeededDuctComponentType>,
  metrics: RadialDuctMetricsType,
  catalog: DuctingComponentProductType[],
  mode: DuctSuppliesModeType
): { runCount: number; complete: boolean } {
  type SizeGroup = {
    runCount: number;
    totalLengthFt: number;
    flexTailCount: number;
    elbowCount: number;
  };
  const groupsBySize = new Map<number, SizeGroup>();
  metrics.rooms.forEach((room) => {
    room.supplies.forEach((run) => {
      if (run.autoSizeIn !== null) {
        const group = groupsBySize.get(run.autoSizeIn) ?? {
          runCount: 0,
          totalLengthFt: 0,
          flexTailCount: 0,
          elbowCount: 0
        };
        group.runCount += 1;
        group.totalLengthFt += Math.max(run.ductLengthFt, 0);
        group.flexTailCount += run.flexTailCount;
        group.elbowCount += run.elbowCount;
        groupsBySize.set(run.autoSizeIn, group);
      }
    });
  });
  if (groupsBySize.size === 0) return { runCount: 0, complete: true };

  let complete = true;
  groupsBySize.forEach((group, sizeIn) => {
    const takeOff = pickTakeOff(catalog, sizeIn, true);
    if (takeOff) {
      addNeeded(target, takeOff, group.runCount);
    } else {
      complete = false;
    }

    const totalTailLengthFt =
      mode === 'metal_flex'
        ? Math.min(
            group.flexTailCount * FLEX_TAIL_LENGTH_FT,
            group.totalLengthFt
          )
        : 0;
    const flexLengthFt =
      mode === 'flex_only' ? group.totalLengthFt : totalTailLengthFt;
    const flexDuct = fittingComponents(catalog, 'flex_duct', sizeIn)[0];
    if (flexDuct && flexLengthFt > 0) {
      addNeeded(
        target,
        flexDuct,
        boxesForLength(
          flexLengthFt,
          flexDuct.ductLengthFt ?? DEFAULT_FLEX_BOX_LENGTH_FT
        )
      );
    } else if (flexLengthFt > 0) {
      complete = false;
    }

    if (mode !== 'flex_only') {
      const metalLengthFt = group.totalLengthFt - totalTailLengthFt;
      const pipe = fittingComponents(catalog, 'round_pipe', sizeIn)[0];
      if (pipe && metalLengthFt > 0) {
        addNeeded(
          target,
          pipe,
          boxesForLength(
            metalLengthFt,
            pipe.ductLengthFt ?? DEFAULT_PIPE_JOINT_LENGTH_FT
          )
        );
      } else if (metalLengthFt > 0) {
        complete = false;
      }
      const elbow = fittingComponents(catalog, 'elbow', sizeIn)[0];
      if (elbow) {
        addNeeded(target, elbow, group.elbowCount);
      } else if (group.elbowCount > 0) {
        complete = false;
      }
      if (mode === 'metal_flex' && group.flexTailCount > 0) {
        complete = false;
      }
    }

    const diffuser = fittingComponents(catalog, 'ceiling_diffuser', sizeIn)[0];
    if (diffuser) {
      addNeeded(target, diffuser, group.runCount);
    } else {
      complete = false;
    }
  });

  return {
    runCount: Array.from(groupsBySize.values()).reduce(
      (total, group) => total + group.runCount,
      0
    ),
    complete
  };
}

function addReturnComponents(
  target: Map<number, NeededDuctComponentType>,
  metrics: RadialDuctMetricsType,
  catalog: DuctingComponentProductType[],
  mode: DuctSuppliesModeType
): { openingCount: number; complete: boolean } {
  const { returnRun } = metrics;
  const totalCfm = returnRun?.cfm ?? 0;
  if (!returnRun || totalCfm <= 0) return { openingCount: 0, complete: true };

  const drawnRunCount = Math.max(returnRun.returns.length, 1);
  const splitOptions = [
    drawnRunCount,
    ...RETURN_SPLIT_OPTIONS.filter((option) => option !== drawnRunCount)
  ];

  let resolved: {
    openings: number;
    sizeIn: number;
    box: DuctingComponentProductType;
    reducer: DuctingComponentProductType | null;
  } | null = null;
  for (const openings of splitOptions) {
    if (openings > 0) {
      const sizeIn = returnBranchSizeIn({
        openings,
        totalCfm,
        plannedOpenings: returnRun.returns.length,
        plannedSizeIn: largestPlannedSizeIn(returnRun.returns),
        mode,
        workingFR: metrics.workingFR
      });
      if (sizeIn !== null) {
        const boxFit = resolveReturnBoxForSize(catalog, sizeIn, mode);
        if (boxFit) {
          resolved = { openings, sizeIn, ...boxFit };
          break;
        }
      }
    }
  }
  if (!resolved) return { openingCount: 0, complete: false };

  let complete = true;
  const { openings, sizeIn, box, reducer } = resolved;
  addNeeded(target, box, openings);
  addNeeded(target, reducer, openings);

  const grille = pickFilterGrilleForBox(catalog, box);
  if (grille) {
    addNeeded(target, grille, openings);
  } else {
    complete = false;
  }

  const takeOff = pickTakeOff(catalog, sizeIn, false);
  if (takeOff) {
    addNeeded(target, takeOff, openings);
  } else {
    complete = false;
  }

  if (mode !== 'flex_only') {
    const pipe = fittingComponents(catalog, 'round_pipe', sizeIn)[0];
    if (pipe) {
      addNeeded(
        target,
        pipe,
        boxesForLength(
          returnRun.ductLengthFt,
          pipe.ductLengthFt ?? DEFAULT_PIPE_JOINT_LENGTH_FT
        )
      );
    } else {
      complete = false;
    }
    const elbow = fittingComponents(catalog, 'elbow', sizeIn)[0];
    if (elbow) {
      addNeeded(target, elbow, returnRun.elbowCount);
    } else if (returnRun.elbowCount > 0) {
      complete = false;
    }
  } else {
    const flexDuct = fittingComponents(catalog, 'flex_duct', sizeIn)[0];
    if (flexDuct) {
      addNeeded(
        target,
        flexDuct,
        boxesForLength(
          returnRun.ductLengthFt,
          flexDuct.ductLengthFt ?? DEFAULT_FLEX_BOX_LENGTH_FT
        )
      );
    } else {
      complete = false;
    }
  }

  return { openingCount: openings, complete };
}

export type NeededDuctComponentsResultType = {
  components: NeededDuctComponentType[];
  supplyRunCount: number;
  returnOpeningCount: number;
  supplyComplete: boolean;
  returnComplete: boolean;
};

export function resolveNeededDuctComponents(
  metrics: RadialDuctMetricsType,
  catalog: DuctingComponentProductType[],
  mode: DuctSuppliesModeType,
  sides: { supply: boolean; return: boolean }
): NeededDuctComponentsResultType {
  const needed = new Map<number, NeededDuctComponentType>();
  let supplyRunCount = 0;
  let returnOpeningCount = 0;
  let supplyComplete = true;
  let returnComplete = true;
  if (sides.supply) {
    const supplyResult = addSupplyComponents(needed, metrics, catalog, mode);
    supplyRunCount = supplyResult.runCount;
    supplyComplete = supplyResult.complete;
  }
  if (sides.return) {
    const returnResult = addReturnComponents(needed, metrics, catalog, mode);
    returnOpeningCount = returnResult.openingCount;
    returnComplete = returnResult.complete;
  }
  return {
    components: Array.from(needed.values()),
    supplyRunCount,
    returnOpeningCount,
    supplyComplete,
    returnComplete
  };
}

export type ZoneDuctingSidePlanType = {
  source: 'kit' | 'components' | 'none';
  kit: NeededDuctingKit | null;
  components: NeededDuctComponentType[];
  componentsTotal: number;
  componentsComplete: boolean;
  runCount: number;
};

export type ZoneDuctingPlanType = {
  supply: ZoneDuctingSidePlanType;
  return: ZoneDuctingSidePlanType;
};

function componentsTotalPrice(components: NeededDuctComponentType[]): number {
  return components.reduce(
    (total, entry) => total + entry.product.price * entry.quantity,
    0
  );
}

function resolveSidePlan(
  kit: NeededDuctingKit | null,
  hasDemand: boolean,
  resolved: {
    components: NeededDuctComponentType[];
    runCount: number;
    complete: boolean;
  } | null
): ZoneDuctingSidePlanType {
  const fittingKit = kit && kit.fitStatus !== 'insufficient' ? kit : null;
  const components = resolved?.components ?? [];
  const componentsTotal = componentsTotalPrice(components);
  const componentsComplete = resolved?.complete ?? false;
  const runCount = resolved?.runCount ?? 0;

  if (!hasDemand) {
    return {
      source: 'none',
      kit: fittingKit,
      components: [],
      componentsTotal: 0,
      componentsComplete: true,
      runCount: 0
    };
  }

  const componentsUsable = components.length > 0 && componentsComplete;
  let source: ZoneDuctingSidePlanType['source'] = 'none';
  if (fittingKit && kit?.fitStatus === 'unverified') {
    source = 'kit';
  } else if (
    fittingKit &&
    (!componentsUsable || fittingKit.product.price <= componentsTotal)
  ) {
    source = 'kit';
  } else if (components.length > 0) {
    source = 'components';
  } else if (fittingKit) {
    source = 'kit';
  }

  return {
    source,
    kit: fittingKit,
    components,
    componentsTotal,
    componentsComplete,
    runCount
  };
}

export function resolveZoneDuctingPlan(
  metrics: RadialDuctMetricsType | null,
  catalog: DuctingComponentProductType[],
  mode: DuctSuppliesModeType,
  needed: NeededZoneDuctingProducts | null
): ZoneDuctingPlanType {
  const supplyDemand =
    metrics?.rooms.some((room) => room.supplies.length > 0) ?? false;
  const returnDemand = (metrics?.returnRun?.cfm ?? 0) > 0;

  const supplyResolved =
    metrics && supplyDemand
      ? (() => {
          const result = resolveNeededDuctComponents(metrics, catalog, mode, {
            supply: true,
            return: false
          });
          return {
            components: result.components,
            runCount: result.supplyRunCount,
            complete: result.supplyComplete
          };
        })()
      : null;
  const returnResolved =
    metrics && returnDemand
      ? (() => {
          const result = resolveNeededDuctComponents(metrics, catalog, mode, {
            supply: false,
            return: true
          });
          return {
            components: result.components,
            runCount: result.returnOpeningCount,
            complete: result.returnComplete
          };
        })()
      : null;

  return {
    supply: resolveSidePlan(
      needed?.supplyKit ?? null,
      supplyDemand,
      supplyResolved
    ),
    return: resolveSidePlan(
      needed?.returnKit ?? null,
      returnDemand,
      returnResolved
    )
  };
}

export function resolveDuctConsumables(
  catalog: DuctingComponentProductType[],
  counts: { supplyRunCount: number; returnOpeningCount: number }
): NeededDuctComponentType[] {
  const totalRuns = counts.supplyRunCount + counts.returnOpeningCount;
  if (totalRuns <= 0) return [];
  const quantity = Math.max(1, Math.ceil(totalRuns / CONSUMABLE_RUNS_PER_UNIT));
  const items: NeededDuctComponentType[] = [];
  const push = (product: DuctingComponentProductType | null): void => {
    if (product) {
      items.push({ product, quantity });
    }
  };
  if (counts.supplyRunCount > 0) {
    push(cheapestByRole(catalog, 'duct_sealant'));
    push(cheapestByRole(catalog, 'cable_ties'));
  }
  if (counts.returnOpeningCount > 0) {
    push(cheapestByRole(catalog, 'foil_tape'));
  }
  const fastenerPack = cheapestByRole(catalog, 'fasteners');
  if (fastenerPack) {
    items.push({ product: fastenerPack, quantity: 1 });
  }
  return items;
}

export type DuctModeStockSummaryType = {
  stockedCount: number;
  totalCount: number;
  missing: string[];
};

export function summarizeSupplyLineStock(
  supplies: ZoneDuctSuppliesType
): DuctModeStockSummaryType {
  type MissingEntry = { count: number; footageFt: number };
  const missingByLabel = new Map<string, MissingEntry>();
  let stockedCount = 0;
  let totalCount = 0;

  [...supplies.supplyRuns, ...supplies.returnRuns].forEach((run) => {
    if (run.coveredByLabel !== null) return;
    run.lines.forEach((line) => {
      if (line.status === undefined) return;
      totalCount += 1;
      if (line.status === 'found') {
        stockedCount += 1;
      } else {
        const entry = missingByLabel.get(line.label) ?? {
          count: 0,
          footageFt: 0
        };
        const footageMatch = line.quantity.match(/^(\d+)'$/);
        if (footageMatch) {
          entry.footageFt += Number(footageMatch[1]);
        } else {
          const numeric = Number(line.quantity);
          entry.count += Number.isFinite(numeric) && numeric > 0 ? numeric : 1;
        }
        missingByLabel.set(line.label, entry);
      }
    });
  });

  const missing = Array.from(missingByLabel.entries()).map(([label, entry]) => {
    if (entry.footageFt > 0) return `${label} × ${entry.footageFt}'`;
    return entry.count > 1 ? `${label} × ${entry.count}` : label;
  });

  return { stockedCount, totalCount, missing };
}

export type AnnotatedZoneDuctSuppliesType = {
  supplies: ZoneDuctSuppliesType;
  missingCount: number;
  notQuotedCount: number;
};

export function annotateSuppliesWithStock(
  supplies: ZoneDuctSuppliesType,
  catalog: DuctingComponentProductType[],
  quotedAccessories?: Array<{ productId: number; quantity: number }>
): AnnotatedZoneDuctSuppliesType {
  let missingCount = 0;
  let notQuotedCount = 0;
  const quotedProductIds = quotedAccessories
    ? new Set(
        quotedAccessories
          .filter((accessory) => accessory.quantity > 0)
          .map((accessory) => accessory.productId)
      )
    : null;

  const annotateRun = (run: DuctRunSuppliesType): DuctRunSuppliesType => {
    if (run.coveredByLabel !== null) return run;
    return {
      ...run,
      lines: run.lines.map((line) => {
        if (line.kind === undefined || line.sizeIn === undefined) {
          if (line.status === 'missing') {
            missingCount += 1;
          }
          return line;
        }
        const stockStatus = lineStockStatus(line.kind, line.sizeIn, catalog);
        if (stockStatus === 'missing') {
          missingCount += 1;
          return { ...line, status: stockStatus };
        }
        if (quotedProductIds) {
          const quotedStatus = lineQuotedStatus(
            line.kind,
            line.sizeIn,
            catalog,
            quotedProductIds
          );
          if (quotedStatus === 'missing') {
            notQuotedCount += 1;
          }
        }
        return { ...line, status: stockStatus };
      })
    };
  };

  return {
    supplies: {
      ...supplies,
      supplyRuns: supplies.supplyRuns.map(annotateRun),
      returnRuns: supplies.returnRuns.map(annotateRun)
    },
    missingCount,
    notQuotedCount
  };
}
